import { useState, useRef, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useDismiss } from '@/hooks/useDismiss'
import { focusRing, quietFill } from '@/components/ui/buttonStyles'

interface DeleteButtonProps {
  label: string
  onDelete: () => Promise<unknown>
  onSuccess: () => void
}

export function DeleteButton({ label, onDelete, onSuccess }: DeleteButtonProps) {
  const [confirming, setConfirming] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [popoverStyle, setPopoverStyle] = useState<{ top: number; right: number }>({ top: 0, right: 0 })
  const popoverRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const dismiss = useCallback(() => setConfirming(false), [])

  useDismiss(popoverRef, dismiss, confirming)

  useEffect(() => {
    if (confirming && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setPopoverStyle({
        top: rect.bottom + window.scrollY + 6,
        right: window.innerWidth - rect.right,
      })
    }
  }, [confirming])

  async function handleConfirm(e: React.MouseEvent) {
    e.stopPropagation()
    setDeleting(true)
    try {
      await onDelete()
      onSuccess()
    } finally {
      setDeleting(false)
      setConfirming(false)
    }
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={(e) => { e.stopPropagation(); setConfirming(true) }}
        className="flex items-center justify-center w-11 h-11 rounded-md bg-btn-danger-bg text-btn-danger-text hover:bg-btn-danger-hover-bg transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-btn-danger-ring focus-visible:ring-offset-2"
        aria-label={label}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 6h18" />
          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
          <path d="M10 11v6" />
          <path d="M14 11v6" />
        </svg>
      </button>

      {confirming && createPortal(
        <div
          ref={popoverRef}
          style={{ top: popoverStyle.top, right: popoverStyle.right }}
          className="fixed z-50 w-52 rounded-lg border border-border-divider bg-card shadow-lg p-3 space-y-2.5"
        >
          <p className="text-xs font-light text-body">
            Are you sure? This cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); setConfirming(false) }}
              className={`rounded-md px-3 py-1.5 text-xs font-normal transition-colors duration-150 ${quietFill} ${focusRing}`}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={deleting}
              className="rounded-md px-3 py-1.5 text-xs font-light bg-btn-danger-confirm-bg text-btn-danger-text hover:bg-btn-danger-confirm-hover-bg disabled:opacity-50 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-btn-danger-ring focus-visible:ring-offset-2"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
